import React, { useState, useEffect, useCallback, useRef } from "react";
import CreatableSelect from "react-select/creatable";
import axios from "axios";

const EditableCategorySelect = ({ value, onChange, onCategoriesChange }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const onCategoriesChangeRef = useRef(onCategoriesChange);

  // Actualizar la referencia cuando cambie el callback
  useEffect(() => {
    onCategoriesChangeRef.current = onCategoriesChange;
  }, [onCategoriesChange]);

  // Función para obtener el token de autenticación
  const getAuthToken = () => {
    try {
      return JSON.parse(localStorage?.session)?.sessionInfo?.token;
    } catch (error) {
      return null;
    }
  };

  // Cargar categorías desde el backend al inicio
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const loadCategories = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          console.error("No hay token de autenticación");
          if (isMounted) {
            setLoading(false);
          }
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/cargar-articulos/get-category-name`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: abortController.signal,
          }
        );

        if (isMounted && response.data) {
          // Asumimos que el backend retorna un array de categorías
          // con estructura { id, name, ... } o similar
          const categoriesData = Array.isArray(response.data) 
            ? response.data 
            : [];
          setCategories(categoriesData);
          
          if (onCategoriesChangeRef.current) {
            onCategoriesChangeRef.current(categoriesData);
          }
        }
      } catch (error) {
        // Solo mostrar error si no fue cancelado intencionalmente
        if (error.name !== 'CanceledError' && error.code !== 'ECONNABORTED' && isMounted) {
          console.error("Error cargando categorías:", error);
        }
        if (isMounted) {
          setLoading(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCategories();

    // Cleanup: cancelar la petición si el componente se desmonta
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []); // Removemos onCategoriesChange de las dependencias

  const handleCreateOption = async (inputValue) => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.error("No hay token de autenticación");
        return;
      }

      // Llamar al endpoint para agregar la categoría
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/cargar-articulos/add-category-name`,
        { name: inputValue },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Recargar las categorías desde el backend
      const categoriesResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/cargar-articulos/get-category-name`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (categoriesResponse.data) {
        const updatedCategories = Array.isArray(categoriesResponse.data) 
          ? categoriesResponse.data 
          : [];
        setCategories(updatedCategories);
        
        // Encontrar la categoría recién creada y seleccionarla
        const newCategory = updatedCategories.find(
          cat => cat.name === inputValue || cat.label === inputValue
        );
        if (newCategory) {
          onChange(String(newCategory.id || newCategory.value || newCategory.name));
        }

        if (onCategoriesChangeRef.current) {
          onCategoriesChangeRef.current(updatedCategories);
        }
      }
    } catch (error) {
      console.error("Error agregando categoría:", error);
      alert("Error al agregar la categoría. Por favor, intenta nuevamente.");
    }
  };

  const handleDeleteOption = useCallback(async (optionValue, e) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      const token = getAuthToken();
      if (!token) {
        console.error("No hay token de autenticación");
        return;
      }

      // Encontrar la categoría a eliminar para obtener su ID
      const categoryToDelete = categories.find(
        cat => String(cat.id || cat.value) === String(optionValue)
      );

      if (!categoryToDelete) {
        console.error("Categoría no encontrada");
        return;
      }

      const categoryId = categoryToDelete.id || categoryToDelete.value || optionValue;

      // Llamar al endpoint para eliminar la categoría
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/cargar-articulos/delete-category-name/${categoryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Recargar las categorías desde el backend
      const categoriesResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/cargar-articulos/get-category-name`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (categoriesResponse.data) {
        const updatedCategories = Array.isArray(categoriesResponse.data) 
          ? categoriesResponse.data 
          : [];
        setCategories(updatedCategories);

        // Si se elimina la categoría seleccionada, limpiar la selección
        if (String(value) === String(optionValue)) {
          onChange("");
        }

        if (onCategoriesChangeRef.current) {
          onCategoriesChangeRef.current(updatedCategories);
        }
      }
    } catch (error) {
      console.error("Error eliminando categoría:", error);
      alert("Error al eliminar la categoría. Por favor, intenta nuevamente.");
    }
  }, [value, onChange, categories]);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.1)" : "none",
      "&:hover": {
        borderColor: "#3b82f6",
      },
    }),
  };

  const formatOptionLabel = ({ value, label }) => (
    <div className="flex items-center justify-between w-full group">
      <span>{label}</span>
      <button
        type="button"
        onClick={(e) => handleDeleteOption(value, e)}
        className="ml-2 text-red-500 hover:text-red-700 text-lg font-bold opacity-0 group-hover:opacity-100 transition-opacity"
        title="Eliminar categoría"
        onMouseDown={(e) => e.preventDefault()}
      >
        ×
      </button>
    </div>
  );

  // Mapear las categorías del backend al formato de react-select
  const options = categories.map((cat) => ({
    value: String(cat.id || cat.value || cat.name),
    label: cat.name || cat.label || String(cat.id),
  }));

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-[300px]">
        {loading ? (
          <div className="flex items-center justify-center py-2">
            <span className="text-sm text-gray-500">Cargando categorías...</span>
          </div>
        ) : (
          <CreatableSelect
            value={selectedOption}
            onChange={(selected) => {
              if (selected) {
                onChange(selected.value);
              } else {
                onChange("");
              }
            }}
            onCreateOption={handleCreateOption}
            options={options}
            formatOptionLabel={formatOptionLabel}
            isClearable
            isSearchable
            placeholder="Seleccionar o escribir nueva categoría..."
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        )}
      </div>
    </div>
  );
};

export default EditableCategorySelect;

